import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import DataGrid, {
  Column, FilterRow, HeaderFilter, SearchPanel, Sorting, Export, Paging, Summary, TotalItem, ColumnChooser
} from "devextreme-react/data-grid";
import { Button } from 'devextreme-react/button';
import DateBox from "devextreme-react/date-box";
import SelectBox from "devextreme-react/select-box";
import notify from 'devextreme/ui/notify';
import 'devextreme/dist/css/dx.material.blue.light.css';
import ExcelJS from "exceljs";
import saveAs from "file-saver";
import { exportDataGrid } from 'devextreme/excel_exporter';
import API_URL from '../config';

// API 엔드포인트 정의
const LIS_REJECT_SUMMARY_API_URL = `${API_URL}/api/oracle/lis-reject-summary`;
const CUST_API_URL = `${API_URL}/api/local/common/custs`;
const DEVICE_API_URL = `${API_URL}/api/local/common/devices`;

export default function LisRejectSummaryPage({ user }) {
  // 날짜 초기값 설정
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  // State 관리
  const [data, setData] = useState([]);
  const [custList, setCustList] = useState([]);
  const [deviceList, setDeviceList] = useState([]);
  
  // 검색 조건 State
  const [selectedFactory] = useState('FT01');
  const [selectedCust, setSelectedCust] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState('ALL');
  const [dateFrom, setDateFrom] = useState(firstDayOfMonth);
  const [dateTo, setDateTo] = useState(today);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  // DataGrid 인스턴스 참조를 위한 ref
  const dataGridRef = useRef(null);

  // 날짜를 'YYYYMMDD' 형식으로 변환
  const formatDate = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    return `${year}${month}${day}`;
  };

  // Factory 변경 시 Cust 목록 조회
  useEffect(() => {
    if (!selectedFactory) return;
    axios.get(CUST_API_URL, { params: { factory: selectedFactory } })
      .then(res => setCustList(res.data.data || []))
      .catch(err => console.error("CUST 목록 조회 실패:", err));
  }, [selectedFactory]);

  // Cust 변경 시 Device 목록 조회
  useEffect(() => {
    if (!selectedFactory || !selectedCust) {
      setDeviceList([]);
      setSelectedDevice('ALL');
      return;
    }
    axios.get(DEVICE_API_URL, { params: { factory: selectedFactory, custCd: selectedCust } })
      .then(res => {
        const fetchedDevices = res.data.data || [];
        const allOption = { CODE_CD: 'ALL' };
        setDeviceList([allOption, ...fetchedDevices]);
        setSelectedDevice('ALL');
      })
      .catch(err => console.error("DEVICE 목록 조회 실패:", err));
  }, [selectedFactory, selectedCust]);

  // 사용자 역할이 'CUST'인 경우, 자동으로 해당 CUST 선택
  useEffect(() => {
    if (user?.role === "CUST" && custList.length > 0) {
      const found = custList.find(c => c.CODE_CD.trim().toUpperCase() === user.username.trim().toUpperCase());
      if (found) setSelectedCust(found.CODE_CD);
    }
  }, [user, custList]);
  
  // 데이터 조회 함수
  const fetchData = useCallback(() => {
    if (!selectedFactory || !selectedCust) {
      notify("Factory와 CUST는 필수 선택 항목입니다.", "warning", 2000);
      return;
    }
    setLoading(true);
    setError("");
    setData([]);

    const params = {
      factory: selectedFactory,
      custCd: selectedCust,
      device: selectedDevice,
      dateFrom: formatDate(dateFrom),
      dateTo: formatDate(dateTo),
    };
    
    axios.get(LIS_REJECT_SUMMARY_API_URL, { params })
      .then(res => {
        if (res.data.retCode !== 0) throw new Error(res.data.retMsg);
        setData(res.data.data.map((row, idx) => ({ ...row, id: idx })));
      })
      .catch(e => {
        const errorMessage = e.response?.data?.retMsg || e.message;
        setError(`조회 오류: ${errorMessage}`);
        notify(errorMessage, 'error', 3000);
        setData([]);
      })
      .finally(() => setLoading(false));
  }, [selectedFactory, selectedCust, selectedDevice, dateFrom, dateTo]);

  // Excel 내보내기 함수
  const onExporting = (e) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("LIS_Reject_Summary");
    exportDataGrid({
        component: e.component,
        worksheet,
        autoFilterEnabled: true
    }).then(() => {
        workbook.xlsx.writeBuffer().then(buffer => {
            saveAs(new Blob([buffer], { type: "application/octet-stream" }), "LIS_Reject_Summary.xlsx");
        });
    });
    e.cancel = true;
  };
  
  return (
    <div style={{ padding: 20, height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* 1. 검색 영역 */}
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ marginBottom: '20px' }}>LIS Reject Summary Report</h3>
        <div style={{ display: "flex", gap: 15, flexWrap: "wrap", alignItems: 'center' }}>
          <DateBox value={dateFrom} displayFormat="yyyy-MM-dd" type="date" onValueChanged={e => setDateFrom(e.value)} label="FROM" width={180} />
          <DateBox value={dateTo} displayFormat="yyyy-MM-dd" type="date" onValueChanged={e => setDateTo(e.value)} label="TO" width={180} />
          <SelectBox items={['FT01']} value={selectedFactory} label="FACTORY" width={150} />
          <SelectBox dataSource={custList} value={selectedCust} valueExpr="CODE_CD" displayExpr="CODE_NM" onValueChanged={e => setSelectedCust(e.value)} disabled={user?.role === "CUST"} searchEnabled showClearButton={user?.role !== "CUST"} placeholder="Select CUST" label="CUST" width={220} />
          <SelectBox dataSource={deviceList} value={selectedDevice} valueExpr="CODE_CD" displayExpr="CODE_CD" onValueChanged={e => setSelectedDevice(e.value)} searchEnabled label="DEVICE" width={220} />
          <Button text="Search" type="default" icon="search" onClick={fetchData} disabled={loading} />
        </div>
        {error && <p style={{ color: "#d9534f", fontWeight: 'bold', marginTop: '10px' }}>{error}</p>}
      </div>
      
      {/* 2. 그리드 영역 */}
      <div style={{ flex: 1, position: 'relative' }}>
        {loading && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255, 255, 255, 0.7)', zIndex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div className="loading-spinner"></div> {/* 별도 CSS로 스피너 스타일 정의 필요 */}
          </div>
        )}
        <DataGrid
          ref={dataGridRef}
          dataSource={data}
          keyExpr="id"
          onExporting={onExporting}
          height="100%"
          showBorders={true}
          allowColumnResizing={true}
          columnAutoWidth={true}
          hoverStateEnabled={true}
        >
          <Export enabled={true} />
          <Paging defaultPageSize={20} />
          <FilterRow visible={true} />
          <HeaderFilter visible={true} />
          <SearchPanel visible={true} width={240} placeholder="Search..." />
          <Sorting mode="multiple" />
          <ColumnChooser enabled={true} />
    
          {/* 컬럼 정의 */}
          <Column dataField="FACTORY" caption="Factory" width={100} fixed={true} />
          <Column dataField="CUSTOMER" caption="Customer" width={120} fixed={true} />
          <Column dataField="MAT_ID" caption="Device" width={150} fixed={true} />
          <Column dataField="LOT_ID" caption="Lot ID" width={150} />
          <Column dataField="TRAN_CODE" caption="Trans Code" width={120} />
          <Column dataField="START_TIME" caption="Start Time"  format="yyyy-MM-dd HH:mm:ss" width={180} />
          <Column dataField="END_TIME" caption="End Time"  format="yyyy-MM-dd HH:mm:ss" width={180} />
          <Column dataField="INPUT" caption="Input" dataType="number" alignment="right" width={100} />
          <Column dataField="GOOD" caption="Good" dataType="number" alignment="right" width={100} />
          <Column dataField="REJECT" caption="Reject" dataType="number" alignment="right" width={100} />
          <Column dataField="YLD" caption="Yield" alignment="right" width={100} />
          <Column dataField="LEAD_QTY" caption="Lead Qty" dataType="number" alignment="right" width={100} />
          <Column dataField="BALL_QTY" caption="Ball Qty" dataType="number" alignment="right" width={100} />
          <Column dataField="OTH_QTY" caption="Other Qty" dataType="number" alignment="right" width={100} />
          <Column dataField="MK_QTY" caption="Mark Qty" dataType="number" alignment="right" width={100} />
          <Column dataField="CV_QTY" caption="CV Qty" dataType="number" alignment="right" width={100} />
          
          {/* 하단 요약 (Summary) */}
          <Summary>
            <TotalItem column="INPUT" summaryType="sum" valueFormat={{ type: 'fixedPoint', precision: 0 }} displayFormat="{0}" />
            <TotalItem column="GOOD" summaryType="sum" valueFormat={{ type: 'fixedPoint', precision: 0 }} displayFormat="{0}" />
            <TotalItem column="REJECT" summaryType="sum" valueFormat={{ type: 'fixedPoint', precision: 0 }} displayFormat="{0}" />
            <TotalItem column="LEAD_QTY" summaryType="sum" valueFormat={{ type: 'fixedPoint', precision: 0 }} displayFormat="{0}" />
            <TotalItem column="BALL_QTY" summaryType="sum" valueFormat={{ type: 'fixedPoint', precision: 0 }} displayFormat="{0}" />
            <TotalItem column="OTH_QTY" summaryType="sum" valueFormat={{ type: 'fixedPoint', precision: 0 }} displayFormat="{0}" />
            <TotalItem column="MK_QTY" summaryType="sum" valueFormat={{ type: 'fixedPoint', precision: 0 }} displayFormat="{0}" />
            <TotalItem column="CV_QTY" summaryType="sum" valueFormat={{ type: 'fixedPoint', precision: 0 }} displayFormat="{0}" />
          </Summary>
        </DataGrid>
      </div>
    </div>
  );
}